import { HttpTransportType, HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { showErrorSnackbar } from '../../component/snackbar/Snackbar.slice';
import { AppDispatch } from '../../app/store';
import {
	AutoReviewFailureMessage,
	AutoReviewSuccessMessage,
	CalculationCancelMessage,
	CalculationFailureMessage,
	CalculationInitiationMessage,
	CalculationSuccessMessage,
	ClientReferenceUploadMessage,
	FileComparisonNotification,
	EnormousFileUploadMessage,
	ImportMessage,
	SettingsNotification,
	StatusWidgetMessage,
} from '../Types/Notifications.types';
import { setIsSignalRConnected, signalRConnected } from '../Slices/Notifications.slice';
import { envProperties } from '../Utils/EnvProperties';
import { getAccessToken } from '@rsmus/react-auth';
import { SignalrHubMethods } from '../../Constants/SignalrHubMethods';
import {
	signalRKeepAliveIntervalInMilliseconds,
	signalRServerTimeoutInMilliseconds,
	signalRTimeout,
} from '../Utils/constants';
import {
	addHandleImportNotification,
} from './trade-signalr.import';
import {
	addCalculationCancelEnableNotification,
	addCalculationCancelDisableNotification,
	addCalculationCancelSuccessNotification,
	addAutoReviewFailureNotification,
	addAutoReviewSuccessNotification,
	addCalculationFailureNotification,
	addCalculationInitiationNotification,
	addCalculationSuccessNotification,
} from './trade-signalr.calculation';
import {
	addClientReferenceFailureNotification,
	addClientReferenceSuccessNotification,
	addUserClientReferenceFailureNotification,
	addUserClientReferenceSuccessNotification,
} from './trade-signalr.clientReference';
import { addSettingsSuccessNotification } from './trade-signalr.settings';
import { addClientStatusWidgetNotification, addUserStatusWidgetNotification } from './trade-signalr.statusWidget';
import { addClientRulesetFailureNotification, addClientRulesetSuccessNotification, addUserClientRulesetFailureNotification, addUserClientRulesetSuccessNotification } from './trade-signalr.clientRuleset';
import { addFileComparisonFailureNotification, addFileComparisonSuccessNotification } from './trade-signalr.filecomparison';
import { addEnormousFileUploadFailureNotification, addEnormousFileUploadSuccessNotification } from './trade-signalr.enormousFileUpload';

export let hubConnection: HubConnection;
let retryCounter: number;

export const createHubConnection = (): HubConnection => {
	hubConnection = new HubConnectionBuilder()
		.withUrl(envProperties.SignalRUrl + 'calculationhub', {
			transport: HttpTransportType.WebSockets,
			accessTokenFactory(): string | Promise<string> {
				return getAccessToken().then((result) => {
					if (result === null) {
						return '';
					}
					return result;
				});
			},
		})
		.withAutomaticReconnect()
		.configureLogging(LogLevel.Information)
		.build();
	return hubConnection;
};

const dispatchError = (dispatch: AppDispatch): void => {
	showErrorSnackbar(dispatch, 'Unable to establish connection with notification service');
};

const start = async (hubConnection: HubConnection, dispatch: AppDispatch): Promise<void> => {
	try {
		await hubConnection.start();
		hubConnection.serverTimeoutInMilliseconds = signalRServerTimeoutInMilliseconds;
		hubConnection.keepAliveIntervalInMilliseconds = signalRKeepAliveIntervalInMilliseconds;
		dispatch(
			signalRConnected({
				hubConnection,
			}),
		);
	} catch {
		setTimeout(() => {
			if (retryCounter < envProperties.retryPolicyCount) {
				start(hubConnection, dispatch);
			}
		}, signalRTimeout);
		retryCounter++;
	}
};

const setupHubCallbacks = (hubConnection: HubConnection, dispatch: AppDispatch, userManager: any, getState: () => any): void => {
	try {
		hubConnection.onreconnected((): void => {
			dispatch(
				signalRConnected({
					hubConnection,
				}),
			);
		});

		hubConnection.onreconnecting(() => {
			dispatch(setIsSignalRConnected(false));
		});
		hubConnection.on(SignalrHubMethods.SettingsSuccessMessage, (settingsSuccessMessage: SettingsNotification) => {
			addSettingsSuccessNotification(settingsSuccessMessage, dispatch);
		});
		hubConnection.on(
			SignalrHubMethods.CalculationInitiationNotification,
			(calculationInitiationMessage: CalculationInitiationMessage) =>
				addCalculationInitiationNotification(calculationInitiationMessage, dispatch, getState),
		);
		hubConnection.on(
			SignalrHubMethods.CalculationSuccessNotification,
			(calculationSuccessMessage: CalculationSuccessMessage) =>
				addCalculationSuccessNotification(calculationSuccessMessage, dispatch, getState),
		);
		hubConnection.on(
			SignalrHubMethods.CalculationFailureNotification,
			(calculationFailureMessage: CalculationFailureMessage) =>
				addCalculationFailureNotification(calculationFailureMessage, dispatch, getState),
		);
		hubConnection.on(SignalrHubMethods.ImportSuccessNotification, (importMessage: ImportMessage) =>
			addHandleImportNotification(importMessage, dispatch),
		);
		hubConnection.on(SignalrHubMethods.ImportFailureNotification, (importMessage: ImportMessage) =>
			addHandleImportNotification(importMessage, dispatch),
		);
		hubConnection.on(
			SignalrHubMethods.ClientReferenceSuccessNotification,
			(clientReferenceUploadMessage: ClientReferenceUploadMessage) =>
				addClientReferenceSuccessNotification(clientReferenceUploadMessage, dispatch),
		);
		hubConnection.on(
			SignalrHubMethods.ClientReferenceFailureNotification,
			(clientReferenceUploadMessage: ClientReferenceUploadMessage) =>
				addClientReferenceFailureNotification(clientReferenceUploadMessage, dispatch),
		);
		hubConnection.on(
			SignalrHubMethods.UserClientReferenceSuccessNotification,
			(clientReferenceUploadMessage: ClientReferenceUploadMessage) =>
				addUserClientReferenceSuccessNotification(clientReferenceUploadMessage, dispatch),
		);
		hubConnection.on(
			SignalrHubMethods.UserClientReferenceFailureNotification,
			(clientReferenceUploadMessage: ClientReferenceUploadMessage) =>
				addUserClientReferenceFailureNotification(clientReferenceUploadMessage, dispatch),
		);
		hubConnection.on(
			SignalrHubMethods.StatusWidgetNotification,
			(statusWidgetMessage: StatusWidgetMessage) =>
				addClientStatusWidgetNotification(statusWidgetMessage, dispatch, userManager),
		);
		hubConnection.on(
			SignalrHubMethods.ClientRulesetSuccessNotification,
			(clientReferenceUploadMessage: ClientReferenceUploadMessage) =>
				addClientRulesetSuccessNotification(clientReferenceUploadMessage, dispatch),
		);
		hubConnection.on(
			SignalrHubMethods.ClientRulesetFailureNotification,
			(clientReferenceUploadMessage: ClientReferenceUploadMessage) =>
				addClientRulesetFailureNotification(clientReferenceUploadMessage, dispatch),
		);
		hubConnection.on(
			SignalrHubMethods.UserClientRulesetSuccessNotification,
			(clientReferenceUploadMessage: ClientReferenceUploadMessage) =>
				addUserClientRulesetSuccessNotification(clientReferenceUploadMessage, dispatch),
		);
		hubConnection.on(
			SignalrHubMethods.UserClientRulesetFailureNotification,
			(clientReferenceUploadMessage: ClientReferenceUploadMessage) =>
				addUserClientRulesetFailureNotification(clientReferenceUploadMessage, dispatch),
		);
		hubConnection.on(
			SignalrHubMethods.AutoReviewSuccessNotification,
			(autoReviewSuccessMessage: AutoReviewSuccessMessage) =>
				addAutoReviewSuccessNotification(autoReviewSuccessMessage, dispatch, getState),
		);
		hubConnection.on(
			SignalrHubMethods.AutoReviewFailureNotification,
			(autoReviewFailureMessage: AutoReviewFailureMessage) =>
				addAutoReviewFailureNotification(autoReviewFailureMessage, dispatch, getState),
		);
		hubConnection.on(
			SignalrHubMethods.CalculationCancelEnableNotification,
			(calculationInitiationMessage: CalculationCancelMessage) =>
				addCalculationCancelEnableNotification(calculationInitiationMessage, dispatch),
		);
		hubConnection.on(
			SignalrHubMethods.CalculationCancelDisableNotification,
			(calculationInitiationMessage: CalculationCancelMessage) =>
				addCalculationCancelDisableNotification(calculationInitiationMessage, dispatch),
		);
		hubConnection.on(
			SignalrHubMethods.CalculationCancelSuccessNotification,
			(calculationCancelMessage: CalculationCancelMessage) =>
				addCalculationCancelSuccessNotification(calculationCancelMessage, dispatch),
		);
		hubConnection.on(
			SignalrHubMethods.FileComparisonFailureNotification,
			(fileComparisonMessage: FileComparisonNotification) =>
				addFileComparisonFailureNotification(fileComparisonMessage, dispatch),
		);
		hubConnection.on(
			SignalrHubMethods.FileComparisonSuccessNotification,
			(fileComparisonMessage: FileComparisonNotification) =>
				addFileComparisonSuccessNotification(fileComparisonMessage, dispatch),
		);
		hubConnection.on(
			SignalrHubMethods.EnormousFileUploadSuccessNotification,
			(enormousFileUploadMessage: EnormousFileUploadMessage) =>
				addEnormousFileUploadSuccessNotification(enormousFileUploadMessage, dispatch),
		);
		hubConnection.on(
			SignalrHubMethods.EnormousFileUploadFailureNotification,
			(enormousFileUploadMessage: EnormousFileUploadMessage) =>
				addEnormousFileUploadFailureNotification(enormousFileUploadMessage, dispatch),
		);
	} catch {
		retryCounter++;
	}
};

export const startHub = (hubConnection: HubConnection, dispatch: AppDispatch, userManager: any, getState: () => any): void => {
	retryCounter = 0;
	setupHubCallbacks(hubConnection, dispatch, userManager, getState);
	start(hubConnection, dispatch);
};
